import { useEffect, useState } from "react";
import api, { eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Calculator, Mail, ChevronDown, ChevronRight } from "lucide-react";
import { EmailQuoteDialog } from "@/components/EmailQuoteDialog";
import { RichQuoteDetail } from "@/components/RichQuoteDetail";

const STATO_COLOR = { Bozza: "outline", "Da Approvare": "secondary", Aperto: "default", Chiuso: "default", Rifiutato: "destructive" };
const fmtDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

export const QuotesForClient = ({ partnerId, prospectId, testid = "client-quotes" }) => {
  const [rows, setRows] = useState(null);
  const [emailQuote, setEmailQuote] = useState(null);
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const { user } = useAuth();
  const canMargin = ["admin", "back_office", "commercial_manager"].includes(user?.role);

  useEffect(() => {
    const p = new URLSearchParams();
    if (partnerId) p.set("partner_id", partnerId);
    if (prospectId) p.set("prospect_id", prospectId);
    if (!partnerId && !prospectId) { setRows([]); return; }
    api.get(`/crm-quotes?${p.toString()}`).then((r) => setRows(r.data.quotes || [])).catch(() => setRows([]));
  }, [partnerId, prospectId]);

  const nImport = rows ? rows.filter((r) => r.source === "importato").length : 0;

  return (
    <Card className="p-6" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 text-left"
        data-testid={`${testid}-toggle`}
      >
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <Calculator className="h-5 w-5 text-primary" />
        <h2 className="font-heading text-lg font-bold tracking-tight">Preventivi</h2>
        {rows && rows.length > 0 && <Badge variant="secondary" className="text-[11px]">{rows.length}</Badge>}
        {nImport > 0 && <Badge variant="outline" className="text-[11px]">{nImport} importati</Badge>}
      </button>

      {open && (
        rows === null ? (
          <Loader2 className="mt-4 h-5 w-5 animate-spin text-primary" />
        ) : rows.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground" data-testid={`${testid}-empty`}>Nessun preventivo collegato a questo cliente.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="w-6 py-2"></th>
                  <th className="py-2 pr-3 font-medium">Numero</th>
                  <th className="py-2 pr-3 font-medium">Origine</th>
                  <th className="py-2 pr-3 font-medium">Data</th>
                  <th className="py-2 pr-3 font-medium">Stato</th>
                  <th className="py-2 pr-3 text-right font-medium">kWp</th>
                  <th className="py-2 pr-3 text-right font-medium">Totale</th>
                  <th className="py-2 text-right font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const key = `${row.source}-${row.id}`;
                  const isOpen = expanded === key;
                  return (
                    <>
                      <tr
                        key={key}
                        className="cursor-pointer border-b border-border/50 hover:bg-muted/30"
                        data-testid={`${testid}-row-${row.id}`}
                        onClick={() => setExpanded(isOpen ? null : key)}
                      >
                        <td className="py-2 text-muted-foreground">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                        <td className="py-2 pr-3 font-mono text-xs font-bold">{row.numero || "—"}</td>
                        <td className="py-2 pr-3">
                          <Badge variant={row.source === "importato" ? "outline" : "secondary"} className="text-[10px]">
                            {row.source === "importato" ? "Importato" : "CRM"}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground">{fmtDate(row.date)}</td>
                        <td className="py-2 pr-3"><Badge variant={STATO_COLOR[row.stato] || "outline"}>{row.stato}</Badge></td>
                        <td className="py-2 pr-3 text-right">{row.kwp_totale ? row.kwp_totale.toLocaleString("it-IT", { maximumFractionDigits: 2 }) : "—"}</td>
                        <td className="py-2 pr-3 text-right font-semibold">{row.totale == null ? "—" : eur(row.totale)}</td>
                        <td className="py-2 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {row.source === "crm" && row.stato === "Aperto" && (
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEmailQuote(row)} data-testid={`${testid}-email-${row.id}`} title="Invia via email"><Mail className="h-4 w-4 text-primary" /></Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr data-testid={`${testid}-detail-${row.id}`}>
                          <td colSpan={8} className="bg-muted/30 p-0">
                            <RichQuoteDetail
                              source={row.source === "importato" ? "torri" : "crm"}
                              id={row.id}
                              canMargin={canMargin}
                              hasPdf={row.source === "importato" ? row.has_pdf : undefined}
                            />
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
      <EmailQuoteDialog quote={emailQuote} open={!!emailQuote} onClose={() => setEmailQuote(null)} />
    </Card>
  );
};
